import { isNullOrUndefined } from "@@/utilities/null-checks";
import type { Except } from "type-fest";
import { z } from "zod";

type MinMaxRange =
    | { readonly min?: number; readonly max?: number }
    | { readonly min: number; readonly max: number }
    | { readonly min?: never; readonly max: number }
    | { readonly min: number; readonly max?: never };

type MinMaxRangeWithRequiredMin = Except<MinMaxRange, "min"> & {
    min: number;
};

/**
 * Make sure value is within a given number range \
 * if smaller round up to given `min` \
 * if larger round down to given `max`
 */
function setWithinRange<T extends number | undefined>(
    value: T,
    range: MinMaxRange,
): T extends undefined ? undefined : number;
function setWithinRange(
    value: number | undefined,
    range: MinMaxRange,
): number | undefined {
    const min = range.min;
    const max = range.max;
    if (isNullOrUndefined(value)) return value;
    if (min && max && min > max)
        throw new Error(
            "Min must be lower than max: " + JSON.stringify({ min, max }),
        );
    if (!isNullOrUndefined(max) && value > max) return max;
    if (!isNullOrUndefined(min) && value < min) return min;
    return value;
}

/**
 * Make sure value is within a range of 0-1 \
 * if smaller round up to 0 \
 * if larger round down to 1
 */
const setWithinPercentageRange = <T extends number | undefined>(
    value: T,
): T => {
    return setWithinRange<T>(value, { min: 0, max: 1 }) as T;
};

const minMaxRangeZodSchema: z.ZodSchema<MinMaxRange> = z
    .object({
        min: z.number().min(0).optional(),
        max: z.number().min(0).optional(),
    })
    .refine(
        value => {
            if (
                typeof value.min !== "undefined" &&
                typeof value.max !== "undefined"
            ) {
                return value.min <= value.max;
            }
            return true;
        },
        { message: "min must be equal or less than max" },
    );

const minMaxWithRequiredMinZodSchema: z.ZodSchema<MinMaxRangeWithRequiredMin> =
    z
        .object({
            min: z.number().min(0),
            max: z.number().min(0).optional(),
        })
        .refine(
            value => {
                if (
                    typeof value.min !== "undefined" &&
                    typeof value.max !== "undefined"
                ) {
                    return value.min <= value.max;
                }
                return true;
            },
            { message: "min must be equal or less than max" },
        );

const minMaxRangeRequiredZodObject = z.object({
    min: z.number().min(0),
    max: z.number().min(0),
});

type MinMaxRangeRequired = z.infer<typeof minMaxRangeRequiredZodObject>;

export {
    minMaxRangeRequiredZodObject,
    minMaxRangeZodSchema,
    minMaxWithRequiredMinZodSchema,
    setWithinPercentageRange,
    setWithinRange,
};
export type { MinMaxRange, MinMaxRangeRequired, MinMaxRangeWithRequiredMin };
